import { Component, Input, Output, EventEmitter, OnChanges, OnInit } from '@angular/core';
import { BzgModalService } from 'bz-genome-angular';
import { DeleteConfirmationComponent } from 'app/shared/components/delete-confirmation/delete-confirmation.component';
import { UserJobsService } from 'app/scheduler/user-Jobs/user-jobs.service';
import { I18nService } from 'i18n/i18n.service';
import { IJob } from '../IJob';
import { IUserJob } from 'app/scheduler/core/IUserJob';
import { ShowToastService } from 'app/shared/services/showToast.service';
import { DateUtilDST } from 'app/core/utils/DateUtilDST';

@Component({
  selector: 'job-settings',
  templateUrl: 'job-settings.component.html',
  styleUrls: ['job-settings.component.scss']
})
export class JobSettingsComponent implements OnChanges, OnInit {

  @Input() applyDisabled = false;
  @Input() userJobs?: Array<IUserJob>
  @Input() currentJob: IJob;
  @Input() jobStates: { [key: string]: { 
    rescheduleJob: boolean, 
    switchActivated: boolean, 
    originalNextRuntimeDate: Date, 
    nextRuntimeDate: Date,
    originalRescheduleJob?: boolean,
    originalSwitchActivated?: boolean
  } } = {};
  @Input() isDevelopment: boolean;
  @Input() areOtherTabsValid: boolean = false;
  @Output() saveJob = new EventEmitter<IJob>();
  @Output() cancel = new EventEmitter();
  @Output() reset = new EventEmitter();
  @Output() validationChange = new EventEmitter<boolean>();

  public showReset: boolean = false;
  public isValidCurrentJobName: boolean = true;
  public validationMessage: string = '';
  public showValidationMessage: boolean = false;
  public dateMax: Date = new Date();
  public showPastDateWarning: boolean = false;

  constructor(private _modalService: BzgModalService,
    private _userJobService: UserJobsService,
    private _showToastService: ShowToastService,
    private _i18nService: I18nService) {
    this.dateMax.setFullYear(this.dateMax.getFullYear() + 10);
  }

  ngOnInit(): void {
    if (this.currentJob) {
      this.updateJobNameValidity(this.currentJob.name);
    }
  }

  ngOnChanges() {
    if (!this.jobStates[this.currentJob.guid]) {
      this.jobStates[this.currentJob.guid] = {
        rescheduleJob: false,
        switchActivated: false,
        originalNextRuntimeDate: new Date(this.currentJob.nextRuntime),
        nextRuntimeDate: new Date(DateUtilDST.formatInUserTimezone(this.currentJob.nextRuntime)),
        originalRescheduleJob: false,
        originalSwitchActivated: false
      };
    }
    
    if (this.currentJob) {
      this.getStatusReset();
      this.updateJobNameValidity(this.currentJob.name);
      this.updateValidationMessage();
      
      // Reset warnings on job change
      this.showPastDateWarning = false;
      
      // Check if the current date is in the past
      if (this.jobStates[this.currentJob.guid]?.nextRuntimeDate) {
        this.checkIfDateIsPast(this.jobStates[this.currentJob.guid].nextRuntimeDate);
      }
    }
  }

  public validateInputJobName(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.updateJobNameValidity(inputElement.value);
  }

  public setJobNow() {
    const newDate = DateUtilDST.formatInUserTimezone(new Date());
    this.jobStates[this.currentJob.guid].nextRuntimeDate = new Date(newDate);
    // Habilitar el job
    this.currentJob.enabled = true;  
  }

  public setJobTo9999() {
    const newDate = new Date(Date.UTC(9999, 11, 31, 23, 59, 59));
    this.jobStates[this.currentJob.guid].nextRuntimeDate = newDate;
  }

  public applyChanges() {
    if (this.jobStates[this.currentJob.guid]) {
      this.currentJob.nextRuntime = DateUtilDST.returnDateToSave(this.jobStates[this.currentJob.guid].nextRuntimeDate);
      if (this.currentJob.schedules) {
        this.currentJob.schedules.forEach(schedule => {
          schedule.nextRunTime = this.currentJob.nextRuntime;
        });
      }
      
      // Actualizar el estado original con los nuevos valores guardados
      this.jobStates[this.currentJob.guid].originalNextRuntimeDate = new Date(this.currentJob.nextRuntime);
      this.jobStates[this.currentJob.guid].nextRuntimeDate = new Date(DateUtilDST.formatInUserTimezone(this.currentJob.nextRuntime));
      
      // Guardar el estado actual de los flags como el nuevo estado original
      this.jobStates[this.currentJob.guid].originalRescheduleJob = this.jobStates[this.currentJob.guid].rescheduleJob;
      this.jobStates[this.currentJob.guid].originalSwitchActivated = this.jobStates[this.currentJob.guid].switchActivated;
    }
    
    this.saveJob.emit(this.currentJob);
    this.getStatusReset();
  }

  public cancelChanges() {
    // Restaurar desde el estado original
    const originalDate = this.jobStates[this.currentJob.guid].originalNextRuntimeDate;
    this.jobStates[this.currentJob.guid].nextRuntimeDate = new Date(originalDate);
    this.currentJob.nextRuntime = originalDate;
    
    // Restaurar el estado original de los flags de reschedule
    this.jobStates[this.currentJob.guid].rescheduleJob = this.jobStates[this.currentJob.guid].originalRescheduleJob || false;
    this.jobStates[this.currentJob.guid].switchActivated = this.jobStates[this.currentJob.guid].originalSwitchActivated || false;
    
    // Resetear advertencias
    this.showPastDateWarning = false;
    
    this.cancel.emit();
  }

  public resetJob() {
    const title = `${this._i18nService.instant('GLOBAL.RESET')} - ${this.currentJob.name}`
    const modal = this._modalService.open(DeleteConfirmationComponent, {
      closeOnBackdropClick: false,
      closeOnRouterNavigation: true,
      data: { title, message: 'GLOBAL.CONFIRM_RESET', button: 'GLOBAL.RESET' }
    });
    modal.afterClosed().subscribe((result) => {
      if (result === 'delete') {
        this._userJobService.resetStatus(this.currentJob.guid).subscribe((response) => {
          if (response) {
            this.reset.emit();
            this.getStatusReset();
            this._showToastService.showToast(this._i18nService.instant('GLOBAL.THE_OPERATION_WAS_SAVED_SUCCESSFULLY'), 'success');
          }
        });
      }
    });
  }

  public getStatusReset() {
    if (this.currentJob.guid) {
      this._userJobService.getStatusReset(this.currentJob.guid).subscribe((response) => {
        this.showReset = response;
      });
    }
  }

  public checkAndSetDate() {
    // Reset warnings
    this.showPastDateWarning = false;

    const jobState = this.jobStates[this.currentJob.guid];
    if (jobState.rescheduleJob) {
      this.setJobNow();
      jobState.switchActivated = true;
    } else {
      this.setJobTo9999();
      jobState.switchActivated = false;
    }
    jobState.nextRuntimeDate = new Date(this.jobStates[this.currentJob.guid].nextRuntimeDate);
    
    // Check if the selected date is in the past
    this.checkIfDateIsPast(jobState.nextRuntimeDate);
  }

  public isNextRuntimeYear9999(): boolean {
    return this.jobStates[this.currentJob.guid].originalNextRuntimeDate.getFullYear() === 9999;
  }

  public shouldShowDateSection(): boolean {
    const jobState = this.jobStates[this.currentJob.guid];
    return !this.isNextRuntimeYear9999() || jobState.rescheduleJob;
  }

  public shouldShowSwitch(): boolean {
    const jobState = this.jobStates[this.currentJob.guid];
    return this.isNextRuntimeYear9999() || jobState.switchActivated;
  }

  public onDateChange(newDate: Date) {
    // Reset warnings
    this.showPastDateWarning = false;

    if (newDate && this.currentJob?.guid) {
      this.jobStates[this.currentJob.guid].nextRuntimeDate = new Date(newDate);
      
      // Check if the selected date is in the past
      this.checkIfDateIsPast(newDate);
    }
  }

  private checkIfDateIsPast(date: Date): void {
    this.showPastDateWarning = DateUtilDST.isDateInPast(date);
  }

  private updateValidationMessage(): void {
    if (this.isDevelopment) {
      this.resetValidationMessage();
      return;
    }

    const hasInvalidJobName = !this.isValidCurrentJobName;
    const hasOtherValidationIssues = !this.areOtherTabsValid;

    this.showValidationMessage = hasInvalidJobName || hasOtherValidationIssues;
    this.validationMessage = this.getValidationMessageKey(hasInvalidJobName, hasOtherValidationIssues);
  }

  private resetValidationMessage(): void {
    this.showValidationMessage = false;
    this.validationMessage = '';
  }

  private getValidationMessageKey(hasInvalidJobName: boolean, hasOtherValidationIssues: boolean): string {
    if (!hasInvalidJobName && !hasOtherValidationIssues) {
      return '';
    }
    
    if (hasInvalidJobName && hasOtherValidationIssues) {
      return 'JOBS.NAME_AND_OTHER_TABS_VALIDATION_MESSAGE';
    }
    
    return hasInvalidJobName 
      ? 'JOBS.NAME_VALIDATION_MESSAGE'
      : 'JOBS.USERJOBS_VALIDATION_ERRORS_IN_OTHER_TABS';
  }

  private updateJobNameValidity(value: string): void {
    this.isValidCurrentJobName = this.validateJobName(value);
    this.updateValidationMessage();
    this.validationChange.emit(this.isValidCurrentJobName);
  }

  private validateJobName(jobName: string): boolean {
    const isValidFormat = /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(jobName);
    const isValidLength = jobName.length >= 1 && jobName.length <= 24;

    if (!isValidFormat || !isValidLength) {
      return false;
    }

    if (this.userJobs && this.currentJob) {
      const nameExists = this.userJobs.some(
        job =>
          job.name.toLowerCase() === jobName.toLowerCase() &&
          job.guid !== this.currentJob.guid
      );
      if (nameExists) {
        return false;
      }
    }

    return true;
  }
}